import { ScrollArea } from "@components/ui/scroll-area";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { IconTrash } from "@tabler/icons-react";
import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { useGetCurrentUserQuery, useGetUsersQuery } from "@/shared/services";
import type {
  IStoriesFolderUser,
  StoriesFolderUserPermEnum,
} from "@/shared/types";
import { MembersDialogUserType } from "./members-dialog-user-type";

interface IProps {
  selectedUsers: IStoriesFolderUser[];
  setSelectedUsers: Dispatch<SetStateAction<IStoriesFolderUser[]>>;
}

export const MembersDialogUser = ({
  selectedUsers,
  setSelectedUsers,
}: IProps) => {
  return (
    <div className="tw-mt-8">
      <span className="tw-mb-4 tw-block tw-font-sans tw-text-base tw-font-normal tw-text-blue">
        Who has access
      </span>
      <ScrollArea maxHeight={192}>
        <ol className="tw-m-0 tw-list-none tw-space-y-3">
          {selectedUsers.map((user) => (
            <SelectedUser
              key={user.oktaId}
              user={user}
              setUserType={(status) =>
                setSelectedUsers((prev) => {
                  const idx = prev.findIndex((el) => el.oktaId === user.oktaId);
                  if (idx >= 0) {
                    const tmp = [...prev];
                    tmp[idx] = {
                      ...tmp[idx],
                      status,
                    };
                    return tmp;
                  }
                  return prev;
                })
              }
              onDelete={() => {
                setSelectedUsers((prev) => {
                  const idx = prev.findIndex((el) => el.oktaId === user.oktaId);
                  if (idx >= 0) {
                    const tmp = [...prev];
                    tmp.splice(idx, 1);
                    return tmp;
                  }
                  return prev;
                });
              }}
            />
          ))}
        </ol>
      </ScrollArea>
    </div>
  );
};

interface ISelectedUserProps {
  user: IStoriesFolderUser;
  onDelete: () => void;
  setUserType: (type: StoriesFolderUserPermEnum) => void;
}

const SelectedUser = ({ user, onDelete, setUserType }: ISelectedUserProps) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allUsers = [] } = useGetUsersQuery();
  const [isPicking, setIsPicking] = useState(false);

  const isCurrentUser = currentUser?.oktaId === user.oktaId;

  const userData = useMemo(() => {
    const data = allUsers.find((el) => el.oktaId === user.oktaId);
    return data;
  }, [allUsers, user]);

  const initials = userData?.firstName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");

  const picture = userData?.profile?.picture;

  return (
    <li className="tw-group tw-flex tw-h-9 tw-items-center tw-gap-3 tw-rounded-lg tw-transition-colors">
      <div className="tw-grid tw-size-8 tw-place-items-center tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-blue/20 tw-bg-blue-200">
        {picture ? (
          <img
            src={picture}
            alt={userData?.firstName}
            className="tw-block tw-size-full tw-object-cover"
          />
        ) : (
          <span className="tw-font-sans tw-text-xs tw-font-semibold tw-uppercase tw-text-blue">
            {initials}
          </span>
        )}
      </div>
      <div className="tw-font-sans tw-text-sm tw-font-normal tw-text-blue">
        {userData?.firstName}
        {isCurrentUser ? " (you)" : ""}
      </div>
      <div
        className={cn("tw-ml-auto", isCurrentUser && "tw-pointer-events-none")}
      >
        <MembersDialogUserType
          isPicking={isPicking}
          setIsPicking={setIsPicking}
          user={user}
          setUserType={setUserType}
        />
      </div>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={cn(
              "tw-grid tw-size-6 tw-place-items-center tw-rounded tw-text-blue-500 tw-opacity-0 tw-outline-none tw-transition-all hover:tw-bg-red-base/15 hover:tw-text-red-600 group-hover:tw-opacity-100",
            )}
            onClick={isCurrentUser ? undefined : onDelete}
          >
            <IconTrash className="tw-size-4" />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content
          className={cn("tw-z-[10000]", !isCurrentUser && "tw-hidden")}
        >
          <Tooltip.Text>You cannot remove yourself</Tooltip.Text>
        </Tooltip.Content>
      </Tooltip>
    </li>
  );
};
